import { formatNativeGoogleCreatives } from './googleNativeCreatives.js';
// API utility functions for AdCanvas
import { projectId, publicAnonKey } from './supabase/info.tsx';
import { EDGE_FUNCTIONS_BASE } from './edge';
import { getAuthHeaders, supabase } from './supabase/client.ts';
import { logError, logWarning, logInfo } from './logger.js';

export class ApiService {
  // Split API endpoints (microservices architecture)
  static healthUrl = `${EDGE_FUNCTIONS_BASE}/adcanvas-health`;
  static facebookUrl = `${EDGE_FUNCTIONS_BASE}/adcanvas-facebook`;
  static googleUrl = `${EDGE_FUNCTIONS_BASE}/adcanvas-google`;
  static pinterestUrl = `${EDGE_FUNCTIONS_BASE}/adcanvas-pinterest`;
  static overviewUrl = `${EDGE_FUNCTIONS_BASE}/adcanvas-overview`;
  static reportsUrl = `${EDGE_FUNCTIONS_BASE}/adcanvas-reports`;
  static proxyUrl = `${EDGE_FUNCTIONS_BASE}/supermetrics-proxy`;
  static imageProxyUrl = `${EDGE_FUNCTIONS_BASE}/image-proxy`;
  static activeRequests = new Map(); // Track active requests to prevent duplicates

  // Legacy baseUrl for backwards compatibility during migration
  static baseUrl = `${EDGE_FUNCTIONS_BASE}/adcanvas-facebook`;

  // Convert an Instagram/Facebook CDN URL to a proxied URL
  // This bypasses the 403 Forbidden issue by fetching server-side
  static getProxiedImageUrl(originalUrl) {
    if (!originalUrl) return null;

    // Only proxy Instagram/Facebook CDN URLs
    const cdnDomains = ['cdninstagram.com', 'fbcdn.net', 'facebook.com'];
    const needsProxy = cdnDomains.some(domain => originalUrl.includes(domain));

    if (!needsProxy) {
      return originalUrl; // Return as-is for non-CDN URLs
    }

    return `${this.imageProxyUrl}?url=${encodeURIComponent(originalUrl)}`;
  }
  
  // Health check endpoint
  static async healthCheck() {
    try {
      const endpoint = `${this.healthUrl}/health`;
      logInfo('ApiService.healthCheck', 'Testing server health', { endpoint, projectId });

      const headers = await getAuthHeaders();
      const response = await fetch(endpoint, {
        method: 'GET',
        headers,
        // Add shorter timeout to prevent hanging
        ...(typeof AbortSignal !== 'undefined' && AbortSignal.timeout ?
          { signal: AbortSignal.timeout(10000) } : {})
      });
      
      if (response.ok) {
        const data = await response.json();
        logInfo('ApiService.healthCheck', 'Server health check passed', { status: response.status });
        return { healthy: true, data };
      } else {
        const errorText = await response.text();
        logWarning('ApiService.healthCheck', 'Server health check failed', { 
          status: response.status, 
          errorText,
          endpoint 
        });
        return { healthy: false, status: response.status, error: errorText };
      }
    } catch (error) {
      logError(
        'ApiService.healthCheck',
        'Server health check exception',
        error,
        {
          endpoint: `${this.healthUrl}/health`,
          projectId
        }
      );
      return { healthy: false, error: error.message };
    }
  }
  
  static async fetchCreatives(params = {}) {
    try {
      logInfo('ApiService.fetchCreatives', 'Fetching creatives from server', { params });

      // Create a unique key for this request
      const requestKey = `creatives-${JSON.stringify(params)}`;

      // Check if this exact request is already in progress
      if (this.activeRequests.has(requestKey)) {
        logInfo('ApiService.fetchCreatives', 'Request already in progress, reusing promise', { requestKey });
        return await this.activeRequests.get(requestKey);
      }

      // Start the request and store the promise
      const requestPromise = this._fetchCreativesInternal(params);
      this.activeRequests.set(requestKey, requestPromise);

      try {
        const result = await requestPromise;
        return result;
      } finally {
        // Clean up the active request
        this.activeRequests.delete(requestKey);
      }

    } catch (error) {
      logError(
        'ApiService.fetchCreatives',
        'Error in fetchCreatives wrapper',
        error,
        { params }
      );
      throw error;
    }
  }
  
  static async _fetchCreativesInternal(params = {}) {
    const endpoint = `${this.facebookUrl}/creatives`;
    try {
      // Build query string
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (key !== 'organizationId' && value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });

      const url = `${endpoint}?${queryParams.toString()}`;
      logInfo('ApiService._fetchCreativesInternal', 'Starting internal fetch', { url, params });

      const headers = { ...await getAuthHeaders(), ...(params.organizationId ? { 'X-Organization-Id': params.organizationId } : {}) };
      const response = await fetch(url, {
        method: 'GET',
        headers,
        // Add shorter timeout to prevent hanging
        ...(typeof AbortSignal !== 'undefined' && AbortSignal.timeout ?
          { signal: AbortSignal.timeout(15000) } : {})
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      logInfo('ApiService._fetchCreativesInternal', 'Successfully loaded data from server', {
        rowCount: data.meta?.rowCount,
        fallback: data.meta?.fallback
      });
      return data;

    } catch (error) {
      logError(
        'ApiService._fetchCreativesInternal',
        'Server unavailable',
        error,
        {
          params,
          endpoint,
          troubleshooting: {
            checkServerHealth: `${this.healthUrl}/health`,
            checkDatabase: `${this.healthUrl}/test-db`,
            expectedUrl: endpoint
          }
        }
      );
      throw error;
    }
  }

  // Get single creative by ID
  static async fetchCreativeById(id) {
    const endpoint = `${this.facebookUrl}/creatives/${id}`;
    try {
      logInfo('ApiService.fetchCreativeById', 'Fetching creative by ID from server', { id, endpoint });

      const headers = await getAuthHeaders();
      const response = await fetch(endpoint, {
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      logInfo('ApiService.fetchCreativeById', 'Successfully loaded creative from server', { id });
      return data;

    } catch (error) {
      logError(
        'ApiService.fetchCreativeById',
        'Server unavailable for creative fetch',
        error,
        { id, endpoint }
      );
      throw error;
    }
  }

  // Get summary statistics
  static async fetchSummaryStats() {
    try {
      console.log('Fetching summary stats from server');

      const url = `${this.facebookUrl}/stats`;
      const headers = await getAuthHeaders();
      const response = await fetch(url, {
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Server error ${response.status}:`, errorText);
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('Successfully loaded stats from server');
      return data;

    } catch (error) {
      console.error('Server unavailable for stats:', error.message);
      throw error;
    }
  }

  // Debug method to check available rollup types
  static async debugRollupTypes() {
    try {
      const url = `${this.healthUrl}/debug-rollup-types`;
      const headers = await getAuthHeaders();
      const response = await fetch(url, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`Debug request failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Debug rollup types failed:', error);
      throw error;
    }
  }

  // Get Facebook rollup data for specific date ranges
  // NOTE: Demo/fallback data has been REMOVED - we always fetch real data or show errors
  static async fetchFacebookRollupData(dateRangeType = 'last_30_days', startDate = null, endDate = null, organizationId = null) {
    console.log(`%c[API] Fetching Facebook rollup data`, 'color: blue; font-weight: bold', { dateRangeType, startDate, endDate });

    try {
      // Create a unique key for this request (include dates for custom ranges)
      const requestKey = startDate && endDate
        ? `rollup-${organizationId || localStorage.getItem('adcanvas_current_org')}-${startDate}-${endDate}`
        : `rollup-${organizationId || localStorage.getItem('adcanvas_current_org')}-${dateRangeType}`;

      // Check if this exact request is already in progress
      if (this.activeRequests.has(requestKey)) {
        console.log(`%c[API] Rollup request already in progress, reusing promise`, 'color: yellow', { requestKey });
        return await this.activeRequests.get(requestKey);
      }

      // Start the request and store the promise
      const requestPromise = this._fetchRollupInternal(dateRangeType, startDate, endDate, organizationId);
      this.activeRequests.set(requestKey, requestPromise);

      try {
        const result = await requestPromise;
        return result;
      } finally {
        // Clean up the active request
        this.activeRequests.delete(requestKey);
      }

    } catch (error) {
      console.error(`%c[API] fetchFacebookRollupData ERROR`, 'color: red; font-weight: bold', error);
      throw error;
    }
  }

  static async _fetchRollupInternal(dateRangeType, startDate = null, endDate = null, organizationId = null) {
    const scopedHeaders = { ...await getAuthHeaders(), ...(organizationId ? { 'X-Organization-Id': organizationId } : {}) };
    const native = await fetch('/api/meta-reporting', {
      method: 'POST', headers: scopedHeaders,
      body: JSON.stringify({ summaryOnly: true, dateRange: dateRangeType, startDate, endDate }),
      signal: AbortSignal.timeout(60000)
    });
    const nativeBody = await native.json();
    if (native.ok) return nativeBody;
    if (native.status !== 409 || nativeBody.code !== 'native_disabled') throw new Error(nativeBody.error || 'Native Meta summary failed');

    const fetchStartTime = Date.now();
    try {
      console.log(`%c[API] _fetchRollupInternal START`, 'color: blue; font-weight: bold', {
        dateRangeType,
        startDate,
        endDate,
        timestamp: new Date().toISOString()
      });

      // Build URL with optional custom date parameters
      let url = `${this.facebookUrl}/facebook-rollup?date_range_type=${dateRangeType}`;
      if (startDate && endDate) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      }
      console.log(`%c[API] Fetching from URL:`, 'color: blue', url);

      const headers = scopedHeaders;
      const response = await fetch(url, {
        headers,
        // Add timeout to prevent hanging
        ...(typeof AbortSignal !== 'undefined' && AbortSignal.timeout ?
          { signal: AbortSignal.timeout(12000) } : {})
      });

      const fetchDuration = Date.now() - fetchStartTime;
      console.log(`%c[API] Response received`, 'color: green', {
        status: response.status,
        ok: response.ok,
        duration: `${fetchDuration}ms`
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`%c[API] ERROR - Server returned ${response.status}`, 'color: red; font-weight: bold', errorText);
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log(`%c[API] SUCCESS - Rollup data received`, 'color: green; font-weight: bold', {
        dateRangeType,
        cost_usd: data.rollupData?.cost_usd,
        fallback: data.meta?.fallback,
        source: data.meta?.source
      });
      return data;

    } catch (error) {
      const fetchDuration = Date.now() - fetchStartTime;
      console.error(`%c[API] FETCH FAILED after ${fetchDuration}ms`, 'color: red; font-weight: bold', {
        dateRangeType,
        errorMessage: error.message,
        errorType: error.name
      });

      // NEVER use fallback data - always throw the error
      throw error;

    }
  }

  /** Fetch all accessible Meta ad objects and their creative inputs, independent of dates. */
  static async fetchMetaInventory(organizationId, { refresh = false } = {}) {
    const response = await fetch('/api/meta-reporting', {
      method: 'POST',
      headers: { ...await getAuthHeaders(), 'X-Organization-Id': organizationId },
      body: JSON.stringify({ inventoryOnly: true, ...(refresh ? { refresh: true } : {}) }),
      signal: AbortSignal.timeout(270000)
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || 'Could not load Meta account inventory');
    return body;
  }

  // Fetch Google Ads creatives (Asset Groups)
  static async fetchGoogleCreatives(params = {}) {
    try {
      logInfo('ApiService.fetchGoogleCreatives', 'Fetching Google Ads data from server', { params });

      // Create a unique key for this request
      const requestKey = `google-creatives-${JSON.stringify(params)}`;

      // Check if this exact request is already in progress
      if (this.activeRequests.has(requestKey)) {
        logInfo('ApiService.fetchGoogleCreatives', 'Request already in progress, reusing promise', { requestKey });
        return await this.activeRequests.get(requestKey);
      }

      // Start the request and store the promise
      const requestPromise = this._fetchGoogleCreativesInternal(params);
      this.activeRequests.set(requestKey, requestPromise);

      try {
        const result = await requestPromise;
        return result;
      } finally {
        // Clean up the active request
        this.activeRequests.delete(requestKey);
      }

    } catch (error) {
      logError(
        'ApiService.fetchGoogleCreatives',
        'Error in fetchGoogleCreatives wrapper',
        error,
        { params }
      );
      throw error;
    }
  }

  static async _fetchGoogleCreativesInternal(params = {}) {
    const endpoint = `${this.googleUrl}/google-ads`;
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      if (params.dateRange) queryParams.append('dateRange', params.dateRange);
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.status) queryParams.append('status', params.status);

      const url = `${endpoint}?${queryParams.toString()}`;
      logInfo('ApiService._fetchGoogleCreativesInternal', 'Starting Google Ads internal fetch', { url, params });

      const headers = { ...await getAuthHeaders(), ...(params.organizationId ? { 'X-Organization-Id': params.organizationId } : {}) };
      const response = await fetch(url, {
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        // 404 is expected for Google Ads endpoints that don't exist yet
        if (response.status === 404) {
          logInfo('ApiService._fetchGoogleCreativesInternal', 'Google Ads endpoint not found (expected)', { status: response.status });
        }
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      logInfo('ApiService._fetchGoogleCreativesInternal', 'Successfully loaded Google Ads data from server', {
        rowCount: data.meta?.rowCount,
        fallback: data.meta?.fallback
      });
      return data;

    } catch (error) {
      // Log more gently for 404s (expected for unimplemented endpoints)
      if (error.message?.includes('404')) {
        logInfo('ApiService._fetchGoogleCreativesInternal', 'Google Ads API not yet implemented', { params });
      } else {
        logError(
          'ApiService._fetchGoogleCreativesInternal',
          'Server unavailable for Google Ads',
          error,
          { params, endpoint }
        );
      }
      throw error;
    }
  }

  // Current configuration and selected-period delivery are independent datasets.
  // A creative inventory failure must not erase verified campaign performance.
  static async fetchGoogleCampaignWorkspace(organization, filters) {
    const customerId = String(organization?.settings?.google_account_id || '').replace(/-/g, '');
    if (!organization?.id || !/^\d{10}$/.test(customerId)) {
      return { report: null, inventory: null, reportError: 'Connect a Google Ads account to view its campaigns.', inventoryError: null };
    }
    const headers = await getAuthHeaders();
    const scope = { organizationId: organization.id, customerId };
    const reportingQuery = new URLSearchParams({ ...scope, compare: 'true' });
    if (filters.dateRange === 'custom') {
      reportingQuery.set('since', filters.customStartDate || '');
      reportingQuery.set('until', filters.customEndDate || '');
    } else reportingQuery.set('preset', filters.dateRange);
    const inventoryQuery = new URLSearchParams({ ...scope, view: 'inventory' });
    const read = async (query, inventory) => {
      const response = await fetch(`/api/google-reporting?${query}`, { headers });
      if (!response.ok) throw new Error('Google reporting unavailable');
      const data = await response.json();
      const returned = inventory ? data : data.account;
      if (returned?.organizationId !== organization.id || returned?.customerId !== customerId) {
        throw new Error('Google reporting scope mismatch');
      }
      return data;
    };
    const [report, inventory] = await Promise.allSettled([
      read(reportingQuery, false), read(inventoryQuery, true),
    ]);
    return {
      report: report.status === 'fulfilled' ? report.value : null,
      inventory: inventory.status === 'fulfilled' ? inventory.value : null,
      reportError: report.status === 'rejected' ? 'Performance reporting is temporarily unavailable.' : null,
      inventoryError: inventory.status === 'rejected' ? 'Current campaign details are temporarily unavailable. Published performance remains available.' : null,
    };
  }

  static async recordGoogleBillingCheck(organization, input) {
    const customerId = String(organization?.settings?.google_account_id || '').replace(/-/g, '');
    if (!organization?.id || !/^\d{10}$/.test(customerId)) throw new Error('Google Ads account is not configured.');
    const response = await fetch('/api/google-billing-check', {
      method:'POST', headers:{...await getAuthHeaders(),'Content-Type':'application/json'},
      body:JSON.stringify({...input,organizationId:organization.id,customerId}),
    });
    if (!response.ok) throw new Error(response.status === 403 ? 'Only AdCanvas administrators can record billing checks.' : 'The billing check could not be saved. Please try again.');
    const result = await response.json();
    if (result.organizationId !== organization.id || result.customerId !== customerId) throw new Error('Billing check account mismatch.');
    return result.billing;
  }

  // Published warehouse facts only: the server selects the reconciled source per day.
  static async fetchGoogleReportingSummary(organization, filters) {
    const customerId = String(organization?.settings?.google_account_id || '').replace(/-/g, '');
    if (!organization?.id || !/^\d{10}$/.test(customerId)) throw new Error('Google reporting account is not configured');
    const query = new URLSearchParams({ organizationId: organization.id, customerId, compare: 'true' });
    if (filters.dateRange === 'custom') {
      query.set('since', filters.customStartDate || ''); query.set('until', filters.customEndDate || '');
    } else query.set('preset', filters.dateRange);
    const response = await fetch(`/api/google-reporting?${query}`, { headers: await getAuthHeaders() });
    if (!response.ok) throw new Error('Google reporting summary is temporarily unavailable');
    const report = await response.json();
    if (report.account?.organizationId !== organization.id || report.account?.customerId !== customerId) throw new Error('Google reporting scope mismatch');
    const mapping = { cost: 'spend', impressions: 'impressions', clicks: 'clicks', ctr: 'ctr', conversions: 'conversions', roas: 'conversionValueRoas' };
    const rollupData = { coverageComplete: report.coverage?.campaigns?.complete === true };
    for (const [display, metric] of Object.entries(mapping)) {
      rollupData[display] = report.totals?.[metric] ?? null;
      rollupData[`comparison_${display}_pct`] = report.comparison?.deltas?.[metric]?.deltaPct ?? null;
    }
    return { rollupData, meta: { source: 'published_google_warehouse', sources: report.sources, window: report.window } };
  }

  static async fetchGoogleNativeCreatives(organization, filters) {
    const customerId = String(organization?.settings?.google_account_id || '').replace(/-/g, '');
    if (!organization?.id || !/^\d{10}$/.test(customerId)) return { active: false };
    const query = new URLSearchParams({ organizationId: organization.id, customerId, view: 'creatives' });
    if (filters.dateRange === 'custom') {
      query.set('since', filters.customStartDate || ''); query.set('until', filters.customEndDate || '');
    } else query.set('preset', filters.dateRange);
    const response = await fetch(`/api/google-reporting?${query}`, { headers: await getAuthHeaders() });
    if (!response.ok) throw new Error('Google creative reporting is temporarily unavailable');
    const report = await response.json();
    if (!report.active) return { active: false };
    if (report.organizationId !== organization.id || report.customerId !== customerId) throw new Error('Google creative scope mismatch');
    return formatNativeGoogleCreatives(report, filters);
  }

  // Get Google Ads rollup data for specific date ranges
  // NOTE: Demo/fallback data has been REMOVED - we always fetch real data or show errors
  static async fetchGoogleRollupData(dateRangeType = 'last_30_days', startDate = null, endDate = null) {
    const fetchStartTime = Date.now();
    try {
      console.log(`%c[API] Fetching Google Ads rollup data`, 'color: blue; font-weight: bold', { dateRangeType, startDate, endDate });

      // Build URL with optional custom date parameters
      let url = `${this.googleUrl}/google-rollup?date_range_type=${dateRangeType}`;
      if (startDate && endDate) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      }
      console.log(`%c[API] Google URL:`, 'color: blue', url);

      const headers = await getAuthHeaders();
      const response = await fetch(url, {
        headers,
      });

      const fetchDuration = Date.now() - fetchStartTime;
      console.log(`%c[API] Google response received`, 'color: green', {
        status: response.status,
        ok: response.ok,
        duration: `${fetchDuration}ms`
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`%c[API] Google ERROR - Status ${response.status}`, 'color: red; font-weight: bold', errorText);
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log(`%c[API] Google SUCCESS`, 'color: green; font-weight: bold', {
        cost: data.rollupData?.cost,
        source: data.meta?.source
      });
      return data;

    } catch (error) {
      const fetchDuration = Date.now() - fetchStartTime;
      console.error(`%c[API] Google FETCH FAILED after ${fetchDuration}ms`, 'color: red; font-weight: bold', {
        errorMessage: error.message,
        errorType: error.name
      });

      // NEVER use fallback data - always throw the error
      throw error;
    }
  }

  // Fetch live data directly from Supermetrics API via proxy
  // organizationSettings should contain: facebook_account_id, facebook_user_id, google_account_id, google_user_id
  static async fetchLiveSupermetricsData(params = {}) {
    const {
      platform = 'meta',
      startDate,
      endDate,
      search = '',
      sortBy = 'roas',
      sortOrder = 'desc',
      limit = 1000,
      minSpend,
      minImpressions,
      adStatus = 'ACTIVE',
      includeDynamicFeed = false,
      organizationSettings = {},
      organizationId = null
    } = params;

    if (!startDate || !endDate) {
      throw new Error('Start Date and End Date are required for live query.');
    }

    const authHeaders = { ...await getAuthHeaders(), ...(organizationId ? { 'X-Organization-Id': organizationId } : {}) };
    let nativeResponse = null;
    if (platform === 'meta') {
      nativeResponse = await fetch('/api/meta-reporting', {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ startDate, endDate }),
        signal: AbortSignal.timeout(290000)
      });
      if (nativeResponse.status === 409) {
        const reason = await nativeResponse.clone().json();
        if (reason.code === 'native_disabled') nativeResponse = null;
      }
      if (nativeResponse && !nativeResponse.ok) {
        throw new Error((await nativeResponse.json()).error || 'Native Meta query failed');
      }
    }

    // Platform-specific configuration
    let dataSourceId;
    let fields;
    let accounts;
    let user;
    let defaultFilter = '';

    if (platform === 'meta') {
      dataSourceId = 'FA';
      fields = "ad_name,creative_title,creative_image_url,adcampaign_name,ad_id,adset_name,campaignstatus,adsetstatus,adstatus,body_asset_text,cost_usd,impressions,link_clicks,link_CTR,CPC,CPM,Frequency,reach,ROAS";
      accounts = organizationSettings.facebook_account_id;
      user = organizationSettings.facebook_user_id;

      if (!nativeResponse && (!accounts || !user)) {
        throw new Error('Facebook Account ID and User ID must be configured in organization settings');
      }

      let filterParts = [];
      if (adStatus && adStatus !== 'ALL') {
        // Filter by ad status AND campaign status AND ad set status
        // This ensures we don't return ads where the campaign or ad set is paused
        filterParts.push(`adstatus == ${adStatus}`);
        filterParts.push(`campaignstatus == ACTIVE`);
        filterParts.push(`adsetstatus == ACTIVE`);
      }
      if (!includeDynamicFeed) {
        filterParts.push(`adcampaign_name !@ Dynamic Feed`);
        filterParts.push(`adcampaign_name !@ DPA`);
        filterParts.push(`adcampaign_name !@ Catalog`);
      }
      defaultFilter = filterParts.join(' AND ');

    } else if (platform === 'google') {
      dataSourceId = 'AW';
      // Note: Supermetrics does NOT provide image URLs for Google Ads
      // All image fields tested return FIELD_NOT_FOUND:
      // - "Ad creative image URL" ❌
      // - "Image 1 (multi asset responsive display)" ❌
      // - "Responsive display ad marketing images" ❌
      // Cards will show icon placeholder instead of images
      fields = [
        // Campaign/Ad Group identifiers
        "Campaignname",
        "Advertisingchanneltype",
        "Advertisingchannelsubtype",
        "Campaignstatus",
        "Adgroupname",
        // Core metrics
        "Cost",
        "Impressions",
        "Clicks",
        "Ctr",
        "CPC",
        "CPM",
        "Conversions",
        "Conversionvalue",
        "ROAS"
      ].join(",");
      accounts = organizationSettings.google_account_id;
      user = organizationSettings.google_user_id;

      if (!nativeResponse && (!accounts || !user)) {
        throw new Error('Google Account ID and User Email must be configured in organization settings');
      }

      defaultFilter = "Campaignstatus == ENABLED";
    } else {
      throw new Error(`Unsupported platform: ${platform}`);
    }

    const requestBody = {
      dataSourceId,
      startDate,
      endDate,
      fields,
      ...(defaultFilter && { filter: defaultFilter }),
      maxRows: limit,
      ...(accounts && { accounts }),
      ...(user && { user }),
      compareType: "prev_range", // Ask for previous period comparison
      compareShow: "value",      // Get comparison values, not percentages
    };

    logInfo('ApiService.fetchLiveSupermetricsData', 'Calling Supermetrics proxy', {
      endpoint: this.proxyUrl,
      platform,
      startDate,
      endDate
    });

    try {
      const response = nativeResponse || await fetch(this.proxyUrl, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(requestBody),
        ...(typeof AbortSignal !== 'undefined' && AbortSignal.timeout ?
          { signal: AbortSignal.timeout(70000) } : {}) // 70 seconds to allow proxy's 60s timeout
      });

      if (!response.ok) {
        // Inside the if (!response.ok) block:
        let errorDetails = `Status code: ${response.status}`;
        let responseBodyText = ''; // To store raw text if JSON parsing fails
        try {
          // First, try to get raw text in case JSON parsing fails later
          responseBodyText = await response.text();
          // Then, try to parse as JSON
          const errorData = JSON.parse(responseBodyText);

          // Log the parsed error data for debugging
          console.log('Parsed error response from proxy:', errorData);

          if (errorData && typeof errorData === 'object') {
            if (errorData.details) {
              // Ensure details is always a string
              errorDetails = typeof errorData.details === 'object' ? JSON.stringify(errorData.details) : String(errorData.details);
            } else if (errorData.error) {
              // Use the error field if details is missing
              errorDetails = typeof errorData.error === 'object' ? JSON.stringify(errorData.error) : String(errorData.error);
            } else {
              // Fallback: stringify the whole object if structure is unknown
              errorDetails = `Unknown error structure: ${JSON.stringify(errorData)}`;
            }
          } else if (responseBodyText) {
            // If parsed data isn't an object but we got text, use the raw text
            errorDetails = responseBodyText;
          }
        } catch (parseError) {
          // If JSON parsing fails, use the raw text if available, otherwise indicate parsing failure
          errorDetails = responseBodyText || `Failed to parse error response body (Status: ${response.status})`;
          logWarning('ApiService.fetchLiveSupermetricsData', 'Failed to parse JSON error response from proxy', { status: response.status, bodyAttempted: responseBodyText });
        }
        // Throw the enhanced error message
        throw new Error(`Proxy error: ${errorDetails}`); // Simplified message, detail is now included
      }

      const rawData = await response.json();
      logInfo('ApiService.fetchLiveSupermetricsData', 'Received data from proxy', { platform });

      // Transform raw Supermetrics data
      const transformedResult = nativeResponse
        ? { items: rawData.records.map(row => this.transformMetaRecord(row)), totalCount: rawData.records.length }
        : this.transformRawSupermetricsData(rawData, platform);
      let finalItems = transformedResult.items;

      // Enrich with cached Storage images/thumbnails from database
      if (platform === 'meta' && finalItems.length > 0) {
        try {
          const adIds = finalItems.map(item => item.id).filter(Boolean);
          const { data: storageData } = await supabase
            .from('adcanvas_facebook_ads')
            .select('ad_id, image_storage_path, image_storage_status, thumbnail_storage_path, thumbnail_storage_status')
            .in('ad_id', adIds)
            .eq('organization_id', authHeaders['X-Organization-Id']);

          if (storageData && storageData.length > 0) {
            // Deduplicate by ad_id (same ad appears multiple times for different date ranges)
            const storageMap = new Map();
            for (const row of storageData) {
              if (!storageMap.has(row.ad_id)) {
                storageMap.set(row.ad_id, row);
              }
            }

            const storageBaseUrl = `https://${projectId}.supabase.co/storage/v1/object/public`;
            finalItems = finalItems.map(item => {
              const storage = storageMap.get(item.id);
              if (!storage) return item;

              const enriched = { ...item, imageStorageStatus: storage.image_storage_status, thumbnailStorageStatus: storage.thumbnail_storage_status };
              if (storage.image_storage_status === 'stored' && storage.image_storage_path) {
                enriched.imageUrl = `${storageBaseUrl}/facebook-ad-images/${storage.image_storage_path}`;
              }
              if (storage.thumbnail_storage_status === 'stored' && storage.thumbnail_storage_path) {
                enriched.thumbnailUrl = `${storageBaseUrl}/facebook-ad-thumbnails/${storage.thumbnail_storage_path}`;
              }
              return enriched;
            });
            logInfo('ApiService.fetchLiveSupermetricsData', 'Enriched items with storage data', { matched: storageMap.size, total: finalItems.length });
          }
        } catch (enrichError) {
          logWarning('ApiService.fetchLiveSupermetricsData', 'Failed to enrich with storage data (non-fatal)', { error: enrichError.message });
        }
      }

      if (nativeResponse) {
        if (adStatus && adStatus !== 'ALL') finalItems = finalItems.filter(item => item.adStatus === adStatus);
        if (!includeDynamicFeed) finalItems = finalItems.filter(item => !/dynamic feed|dpa|catalog/i.test(item.campaignName || ''));
      }

      // Apply client-side filtering/sorting
      if (search.trim()) {
        const searchTerm = search.trim().toLowerCase();
        finalItems = finalItems.filter(item => {
          if (platform === 'meta') {
            return (item.adName?.toLowerCase().includes(searchTerm) ||
                   item.creativeTitle?.toLowerCase().includes(searchTerm) ||
                   item.campaignName?.toLowerCase().includes(searchTerm) ||
                   item.adSetName?.toLowerCase().includes(searchTerm) ||
                   item.bodyAssetText?.toLowerCase().includes(searchTerm));
          } else if (platform === 'google') {
            return (item.assetGroupName?.toLowerCase().includes(searchTerm) ||
                   item.campaignName?.toLowerCase().includes(searchTerm));
          }
          return false;
        });
      }
      if (minSpend && minSpend > 0) finalItems = finalItems.filter(item => item.metrics.cost?.current >= minSpend);
      if (minImpressions && minImpressions > 0) finalItems = finalItems.filter(item => item.metrics.impressions?.current >= minImpressions);

      // Apply sorting
      finalItems.sort((a, b) => {
        const metricKeyA = a.metrics[sortBy];
        const metricKeyB = b.metrics[sortBy];
        const valA = metricKeyA?.current ?? (sortOrder === 'asc' ? Infinity : -Infinity);
        const valB = metricKeyB?.current ?? (sortOrder === 'asc' ? Infinity : -Infinity);

        if (sortBy === 'title' || sortBy === 'adName' || sortBy === 'assetGroupName') {
          const nameA = (platform === 'meta' ? a.adName : a.assetGroupName) || '';
          const nameB = (platform === 'meta' ? b.adName : b.assetGroupName) || '';
          return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        }

        return sortOrder === 'asc' ? valA - valB : valB - valA;
      });

      return {
        items: finalItems,
        meta: {
          dateRange: `${startDate}..${endDate}`,
          comparisonRange: nativeResponse ? rawData.comparisonRange : null,
          rowCount: finalItems.length,
          totalCount: transformedResult.totalCount,
          hasMore: false,
          generatedAt: new Date().toISOString(),
          fallback: false,
          source: nativeResponse ? 'meta_direct' : 'live_supermetrics',
          query: params,
          clientFilteredCount: finalItems.length
        }
      };

    } catch (error) {
      logError('ApiService.fetchLiveSupermetricsData', 'Failed to fetch live data via proxy', error, {
        platform,
        startDate,
        endDate
      });
      throw error;
    }
  }

  // Transform raw Supermetrics data to dashboard format
  static transformRawSupermetricsData(rawData, platform) {
    // Check if rawData is completely missing or malformed
    if (!rawData || !rawData.data) {
      logWarning('ApiService.transformRawSupermetricsData', 'Received invalid or malformed data from Supermetrics', {
        platform,
        dataReceived: rawData
      });
      return { items: [], totalCount: 0 };
    }

    // Check if the query was successful but returned no results
    if (rawData.data.length === 0) {
      const isSuccessful = rawData.meta?.status_code === 'SUCCESS';
      if (isSuccessful) {
        logInfo('ApiService.transformRawSupermetricsData', 'Supermetrics query successful but returned no data for the selected date range', {
          platform,
          dateRange: rawData.meta?.query?.start_date && rawData.meta?.query?.end_date 
            ? `${rawData.meta.query.start_date} to ${rawData.meta.query.end_date}`
            : 'unknown',
          totalRows: rawData.meta?.result?.total_rows || 0
        });
      } else {
        logWarning('ApiService.transformRawSupermetricsData', 'Received empty data array from Supermetrics', {
          platform,
          statusCode: rawData.meta?.status_code,
          dataReceived: rawData
        });
      }
      return { items: [], totalCount: 0 };
    }

    // Check if we only have headers but no data rows
    if (rawData.data.length < 2) {
      logInfo('ApiService.transformRawSupermetricsData', 'Supermetrics returned headers but no data rows', {
        platform,
        headers: rawData.data[0]
      });
      return { items: [], totalCount: 0 };
    }

    const headers = rawData.data[0];
    const rows = rawData.data.slice(1);
    const totalCount = rows.length;

    logInfo('ApiService.transformRawSupermetricsData', `Transforming ${totalCount} rows for ${platform}`, {
      headers: headers.slice(0, 10)
    });

    // Header to field mapping
    // Note: Supermetrics may return different header names for the same field
    const headerMap = {
      'Ad name': 'ad_name',
      'Creative title': 'creative_title',
      'Body copy': 'body_asset_text',
      'Ad creative image URL': 'ad_creative_image_url',
      'Creative image URL': 'ad_creative_image_url',  // Alternative header name
      'Image URL': 'ad_creative_image_url',  // Another possible header name
      'Ad campaign name': 'campaign_name',
      'Ad set name': 'adset_name',
      'Ad ID': 'ad_id',
      'Ad status': 'ad_status',
      'Ad preview URL: Instagram story': 'instagram_story_preview_url',
      'Ad preview URL: Instagram': 'instagram_preview_url',
      'Ad preview URL: Facebook story': 'facebook_story_preview_url',
      'Ad preview URL: mobile feed': 'mobile_feed_preview_url',
      'Ad preview URL: desktop feed': 'desktop_feed_preview_url',
      'Ad preview URL: mobile interstitial': 'mobile_interstitial_preview_url',
      'Ad preview URL: mobile banner': 'mobile_banner_preview_url',
      'Ad preview URL: right column': 'right_column_preview_url',
      'Cost (USD)': 'cost_usd',
      'Impressions': 'impressions',
      'Unique link clicks': 'unique_link_clicks',
      'CTR (link click-through rate)': 'ctr_link_click_through_rate',
      'CPC (all)': 'cpc_all',
      'CPM (cost per 1000 impressions)': 'cpm_cost_per_1000_impressions',
      'Frequency': 'frequency',
      'Reach': 'reach',
      'Quality ranking': 'quality_ranking',
      'Engagement rate ranking': 'engagement_rate_ranking',
      'Return on ad spend (ROAS)': 'roas',
      // Comparison value headers for Meta
      'Comparison value Cost (USD)': 'comparison_cost_usd',
      'Comparison value Impressions': 'comparison_impressions',
      'Comparison value Unique link clicks': 'comparison_unique_link_clicks',
      'Comparison value CTR (link click-through rate)': 'comparison_ctr_link_click_through_rate',
      'Comparison value CPC (all)': 'comparison_cpc_all',
      'Comparison value CPM (cost per 1000 impressions)': 'comparison_cpm_cost_per_1000_impressions',
      'Comparison value Frequency': 'comparison_frequency',
      'Comparison value Reach': 'comparison_reach',
      'Comparison value Quality ranking': 'comparison_quality_ranking',
      'Comparison value Engagement rate ranking': 'comparison_engagement_rate_ranking',
      'Comparison value Return on ad spend (ROAS)': 'comparison_roas',
      // Google Ads Campaign Mapping
      'Campaign name': 'campaign_name_google',
      'Campaign type': 'campaign_type',
      'Advertising channel type': 'campaign_type',
      'Advertising channel subtype': 'campaign_subtype',
      'Campaign status': 'campaign_status',
      // Ad Group fields
      'Ad group name': 'ad_group_name',
      'Adgroupname': 'ad_group_name',
      // Metrics
      'Cost': 'cost_google',
      'Clicks': 'clicks',
      'CTR': 'ctr_google',
      'CPC': 'cpc',
      'CPM': 'cpm',
      'Conversions': 'conversions',
      'Conversion value': 'conversion_value',
      'Conv. value / cost': 'roas_google',
      'Conv. value/cost': 'roas_google',
      'Return on ad spend (ROAS)': 'roas_google',
      'ROAS': 'roas_google',
      // Google Ads comparison values
      'Comparison value Cost': 'comparison_cost_google',
      'Comparison value Clicks': 'comparison_clicks',
      'Comparison value CTR': 'comparison_ctr_google',
      'Comparison value CPC': 'comparison_cpc',
      'Comparison value CPM': 'comparison_cpm',
      'Comparison value Conversions': 'comparison_conversions',
      'Comparison value Conversion value': 'comparison_conversion_value',
      'Comparison value Conv. value / cost': 'comparison_roas_google',
      'Comparison value Conv. value/cost': 'comparison_roas_google',
      'Comparison value ROAS': 'comparison_roas_google'
    };

    const items = rows.map((row, rowIndex) => {
      try {
        const record = {};
        headers.forEach((header, index) => {
          const mappedKey = headerMap[header];
          if (mappedKey) {
            record[mappedKey] = row[index];
          }
        });

        // Platform-specific transformation
        if (platform === 'meta') {
          const mappedRecord = {
            ...record,
            roas: parseFloat(record.roas || '0') || 0,
            cost_usd: parseFloat(record.cost_usd || '0') || 0,
            impressions: parseInt(record.impressions || '0', 10) || 0,
            reach: parseInt(record.reach || '0', 10) || 0,
            unique_link_clicks: parseInt(record.unique_link_clicks || '0', 10) || 0,
            ctr_link_click_through_rate: parseFloat(record.ctr_link_click_through_rate || '0') || 0,
            cpc_all: parseFloat(record.cpc_all || '0') || 0,
            cpm_cost_per_1000_impressions: parseFloat(record.cpm_cost_per_1000_impressions || '0') || 0,
            frequency: parseFloat(record.frequency || '0') || 0,
            quality_ranking: record.quality_ranking ? parseInt(record.quality_ranking, 10) : null,
            engagement_rate_ranking: record.engagement_rate_ranking ? parseInt(record.engagement_rate_ranking, 10) : null,
            // Parse comparison values from Supermetrics response
            comparison_roas: parseFloat(record.comparison_roas || '0') || null,
            comparison_cost_usd: parseFloat(record.comparison_cost_usd || '0') || null,
            comparison_impressions: parseInt(record.comparison_impressions || '0', 10) || null,
            comparison_reach: parseInt(record.comparison_reach || '0', 10) || null,
            comparison_unique_link_clicks: parseInt(record.comparison_unique_link_clicks || '0', 10) || null,
            comparison_ctr_link_click_through_rate: parseFloat(record.comparison_ctr_link_click_through_rate || '0') || null,
            comparison_cpc_all: parseFloat(record.comparison_cpc_all || '0') || null,
            comparison_cpm_cost_per_1000_impressions: parseFloat(record.comparison_cpm_cost_per_1000_impressions || '0') || null,
            comparison_frequency: parseFloat(record.comparison_frequency || '0') || null,
            comparison_quality_ranking: record.comparison_quality_ranking ? parseInt(record.comparison_quality_ranking, 10) : null,
            comparison_engagement_rate_ranking: record.comparison_engagement_rate_ranking ? parseInt(record.comparison_engagement_rate_ranking, 10) : null,
            ad_id: record.ad_id || `live_${platform}_${rowIndex}`,
            ad_name: record.ad_name || 'Untitled Ad',
            ad_creative_image_url: record.ad_creative_image_url || ''
          };

          // Use the transformer from server
          return this.transformMetaRecord(mappedRecord);

        } else if (platform === 'google') {
          // Note: Supermetrics does NOT provide image URLs for Google Ads
          // Cards will show icon placeholder instead
          const campaignName = record.campaign_name_google || 'Unknown Campaign';
          const mappedRecord = {
            ...record,
            campaign_name: campaignName,
            // For live query, use campaign name as asset group name (campaign-level data)
            asset_group_name: campaignName,
            campaign_status: record.campaign_status,
            campaign_type: record.campaign_type || 'UNKNOWN',
            campaign_subtype: record.campaign_subtype || null,
            ad_group_name: record.ad_group_name || null,
            asset_urls: [],
            // Metrics
            roas: parseFloat(record.roas_google || '0') || 0,
            cost: parseFloat(record.cost_google || '0') || 0,
            impressions: parseInt(record.impressions || '0', 10) || 0,
            clicks: parseInt(record.clicks || '0', 10) || 0,
            ctr: parseFloat(record.ctr_google || '0') || 0,
            cpc: parseFloat(record.cpc || '0') || 0,
            cpm: parseFloat(record.cpm || '0') || 0,
            conversions: parseFloat(record.conversions || '0') || 0,
            conversion_value: parseFloat(record.conversion_value || '0') || 0,
            // Comparison values
            comparison_roas: parseFloat(record.comparison_roas_google || '0') || null,
            comparison_cost: parseFloat(record.comparison_cost_google || '0') || null,
            comparison_impressions: parseInt(record.comparison_impressions || '0', 10) || null,
            comparison_clicks: parseInt(record.comparison_clicks || '0', 10) || null,
            comparison_ctr: parseFloat(record.comparison_ctr_google || '0') || null,
            comparison_cpc: parseFloat(record.comparison_cpc || '0') || null,
            comparison_cpm: parseFloat(record.comparison_cpm || '0') || null,
            comparison_conversions: parseFloat(record.comparison_conversions || '0') || null,
            // Use campaign name as ID for campaign-level data
            campaign_id: `campaign_${rowIndex}`
          };

          return this.transformGoogleRecord(mappedRecord);
        } else {
          logWarning('ApiService.transformRawSupermetricsData', `No transformation logic for platform: ${platform}`, { rowIndex });
          return null;
        }
      } catch (transformError) {
        logError('ApiService.transformRawSupermetricsData', `Error transforming row ${rowIndex}`, transformError, {
          rowData: row,
          headers,
          platform
        });
        return null;
      }
    }).filter(item => item !== null);

    return { items, totalCount };
  }

  // Transform Meta record (replicating server logic)
  static transformMetaRecord(record) {
    const calculateDelta = (current, prior) => {
      if (prior === null || prior === undefined) {
        return { delta: null, deltaPct: null };
      }
      const delta = current - prior;
      const deltaPct = prior !== 0 ? delta / prior : null;
      return { delta, deltaPct };
    };

    return {
      id: record.ad_id,
      adName: record.ad_name,
      creativeTitle: record.creative_title,
      bodyAssetText: record.body_asset_text,
      imageUrl: record.ad_creative_image_url,
      instagramStoryPreviewUrl: record.instagram_story_preview_url,
      instagramPreviewUrl: record.instagram_preview_url,
      facebookStoryPreviewUrl: record.facebook_story_preview_url,
      mobileFeedPreviewUrl: record.mobile_feed_preview_url,
      desktopFeedPreviewUrl: record.desktop_feed_preview_url,
      mobileInterstitialPreviewUrl: record.mobile_interstitial_preview_url,
      mobileBannerPreviewUrl: record.mobile_banner_preview_url,
      rightColumnPreviewUrl: record.right_column_preview_url,
      campaignName: record.campaign_name || 'Unknown Campaign',
      adSetName: record.adset_name || 'Unknown Ad Set',
      adStatus: record.ad_status,
      metrics: {
        roas: {
          current: record.roas,
          prior: record.comparison_roas,
          ...calculateDelta(record.roas, record.comparison_roas)
        },
        cost: {
          current: record.cost_usd,
          prior: record.comparison_cost_usd,
          ...calculateDelta(record.cost_usd, record.comparison_cost_usd)
        },
        impressions: {
          current: record.impressions,
          prior: record.comparison_impressions,
          ...calculateDelta(record.impressions, record.comparison_impressions)
        },
        reach: {
          current: record.reach,
          prior: record.comparison_reach,
          ...calculateDelta(record.reach, record.comparison_reach)
        },
        uniqueLinkClicks: {
          current: record.unique_link_clicks,
          prior: record.comparison_unique_link_clicks,
          ...calculateDelta(record.unique_link_clicks, record.comparison_unique_link_clicks)
        },
        ctr: {
          current: record.ctr_link_click_through_rate,
          prior: record.comparison_ctr_link_click_through_rate,
          ...calculateDelta(record.ctr_link_click_through_rate, record.comparison_ctr_link_click_through_rate)
        },
        cpc: {
          current: record.cpc_all,
          prior: record.comparison_cpc_all,
          ...calculateDelta(record.cpc_all, record.comparison_cpc_all)
        },
        cpm: {
          current: record.cpm_cost_per_1000_impressions,
          prior: record.comparison_cpm_cost_per_1000_impressions,
          ...calculateDelta(record.cpm_cost_per_1000_impressions, record.comparison_cpm_cost_per_1000_impressions)
        },
        frequency: {
          current: record.frequency,
          prior: record.comparison_frequency,
          ...calculateDelta(record.frequency, record.comparison_frequency)
        },
        qualityRanking: {
          current: record.quality_ranking,
          prior: record.comparison_quality_ranking,
          delta: null,
          deltaPct: null
        },
        engagementRanking: {
          current: record.engagement_rate_ranking,
          prior: record.comparison_engagement_rate_ranking,
          delta: null,
          deltaPct: null
        }
      }
    };
  }

  // Transform Google record (handles both campaign and asset group data)
  static transformGoogleRecord(record) {
    const calculateDelta = (current, prior) => {
      if (prior === null || prior === undefined) {
        return { delta: null, deltaPct: null };
      }
      const delta = current - prior;
      const deltaPct = prior !== 0 ? delta / prior : null;
      return { delta, deltaPct };
    };

    // Handle asset_urls for asset group level data
    let assetUrls = [];
    if (Array.isArray(record.asset_urls)) {
      assetUrls = record.asset_urls;
    } else if (typeof record.asset_urls === 'string') {
      try {
        assetUrls = JSON.parse(record.asset_urls);
      } catch (e) {
        console.warn('Failed to parse asset_urls as JSON:', e);
        assetUrls = [];
      }
    }

    return {
      // Use campaign_id for campaign-level data, asset_group_id for asset groups
      id: record.campaign_id || record.asset_group_id,
      campaignName: record.campaign_name || 'Unknown Campaign',
      campaignType: record.campaign_type || '',
      campaignStatus: record.campaign_status,
      assetGroupName: record.asset_group_name,
      asset_urls: assetUrls,
      metrics: {
        roas: {
          current: record.roas,
          prior: record.comparison_roas,
          ...calculateDelta(record.roas, record.comparison_roas)
        },
        cost: {
          current: record.cost,
          prior: record.comparison_cost,
          ...calculateDelta(record.cost, record.comparison_cost)
        },
        impressions: {
          current: record.impressions,
          prior: record.comparison_impressions,
          ...calculateDelta(record.impressions, record.comparison_impressions)
        },
        clicks: {
          current: record.clicks,
          prior: record.comparison_clicks,
          ...calculateDelta(record.clicks, record.comparison_clicks)
        },
        ctr: {
          current: record.ctr,
          prior: record.comparison_ctr,
          ...calculateDelta(record.ctr, record.comparison_ctr)
        },
        cpc: {
          current: record.cpc,
          prior: record.comparison_cpc,
          ...calculateDelta(record.cpc, record.comparison_cpc)
        },
        cpm: {
          current: record.cpm,
          prior: record.comparison_cpm,
          ...calculateDelta(record.cpm, record.comparison_cpm)
        },
        conversions: {
          current: record.conversions,
          prior: record.comparison_conversions,
          ...calculateDelta(record.conversions, record.comparison_conversions)
        }
      }
    };
  }

  // Mint Meta placement previews for one ad. Meta preview links expire within days, so the
  // server decides freshness and returns only links that are safe to frame.
  static async fetchAdPreviews(adId, organizationId, { force = false } = {}) {
    const response = await fetch('/api/meta-reporting', {
      method: 'POST',
      headers: { ...await getAuthHeaders(), 'X-Organization-Id': organizationId },
      body: JSON.stringify({ previewAdId: String(adId), force }),
      signal: AbortSignal.timeout(60000)
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || 'Preview request failed');
    return body.previews || {};
  }

  // Helper: Get default start date (14 days ago - reduced from 60 for efficiency)
  static getDefaultStartDate() {
    const date = new Date();
    date.setDate(date.getDate() - 14);
    return date.toISOString().split('T')[0];
  }

  // Helper: Get default end date (today)
  static getDefaultEndDate() {
    return new Date().toISOString().split('T')[0];
  }

  // Helper: Calculate date range for a preset (mirrors date-utils.ts logic)
  static getDateRangeForPreset(preset) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayOfWeek = today.getDay();

    const toDateStr = (d) => d.toISOString().split('T')[0];

    switch (preset) {
      case 'today':
        return { startDate: toDateStr(today), endDate: toDateStr(today) };

      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return { startDate: toDateStr(yesterday), endDate: toDateStr(yesterday) };
      }

      case 'last_7_days': {
        const endDate = new Date(today);
        endDate.setDate(today.getDate() - 1);
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 6);
        return { startDate: toDateStr(startDate), endDate: toDateStr(endDate) };
      }

      case 'last_30_days': {
        const endDate = new Date(today);
        endDate.setDate(today.getDate() - 1);
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 29);
        return { startDate: toDateStr(startDate), endDate: toDateStr(endDate) };
      }

      case 'week_to_date': {
        const thisSunday = new Date(today);
        thisSunday.setDate(today.getDate() - dayOfWeek);
        return { startDate: toDateStr(thisSunday), endDate: toDateStr(today) };
      }

      case 'last_week_sun_sat':
      default: {
        const lastSunday = new Date(today);
        lastSunday.setDate(today.getDate() - dayOfWeek - 7);
        const lastSaturday = new Date(lastSunday);
        lastSaturday.setDate(lastSunday.getDate() + 6);
        return { startDate: toDateStr(lastSunday), endDate: toDateStr(lastSaturday) };
      }
    }
  }

  // Fetch fresh image URL for a single ad
  // This is used by CreativeCard to get non-expired image URLs on-demand when cached ones fail
  static async fetchFreshImageUrl(adId, options = {}) {
    const {
      startDate = this.getDefaultStartDate(),
      endDate = this.getDefaultEndDate(),
      organizationSettings = {}
    } = options;

    if (!organizationSettings.facebook_account_id || !organizationSettings.facebook_user_id) {
      throw new Error('Facebook Account ID and User ID must be configured in organization settings');
    }

    // Minimal fields - only ad_id and creative_image_url for fast response
    const imageFields = 'ad_id,creative_image_url';

    const requestBody = {
      dataSourceId: 'FA',
      startDate,
      endDate,
      fields: imageFields,
      filter: `ad_id == ${adId}`,
      maxRows: 1,
      accounts: organizationSettings.facebook_account_id,
      user: organizationSettings.facebook_user_id
    };

    logInfo('ApiService.fetchFreshImageUrl', 'Fetching fresh image URL', {
      adId,
      startDate,
      endDate
    });

    try {
      const response = await fetch(this.proxyUrl, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(requestBody),
        // Increased timeout for Supermetrics API (90 seconds)
        ...(typeof AbortSignal !== 'undefined' && AbortSignal.timeout ?
          { signal: AbortSignal.timeout(90000) } : {})
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Proxy error: ${response.status} - ${errorText}`);
      }

      const rawData = await response.json();

      // Check if we got any data
      if (!rawData?.data || rawData.data.length < 2) {
        logWarning('ApiService.fetchFreshImageUrl', 'No data returned for ad', { adId });
        return {
          success: false,
          error: 'No image data found for this ad',
          imageUrl: null
        };
      }

      // Parse headers and first data row
      const headers = rawData.data[0];
      const row = rawData.data[1];

      // Find the image URL in the response
      // Supermetrics may return different header names
      const imageHeaderVariants = [
        'Ad creative image URL',
        'Creative image URL',
        'Image URL'
      ];

      let imageUrl = null;
      for (const headerVariant of imageHeaderVariants) {
        const headerIndex = headers.indexOf(headerVariant);
        if (headerIndex !== -1 && row[headerIndex]) {
          imageUrl = row[headerIndex];
          break;
        }
      }

      if (!imageUrl) {
        logWarning('ApiService.fetchFreshImageUrl', 'No image URL in response', { adId, headers });
        return {
          success: false,
          error: 'Image URL not found in response',
          imageUrl: null
        };
      }

      logInfo('ApiService.fetchFreshImageUrl', 'Successfully fetched fresh image URL', {
        adId,
        hasUrl: !!imageUrl
      });

      return {
        success: true,
        imageUrl,
        error: null
      };

    } catch (error) {
      logError('ApiService.fetchFreshImageUrl', 'Failed to fetch image URL', error, { adId });
      return {
        success: false,
        error: error.message || 'Failed to fetch fresh image URL',
        imageUrl: null
      };
    }
  }

  // Fetch overview data (aggregated cross-platform metrics)
  // For preset ranges: dateRange = 'yesterday', 'last_week_sun_sat', etc.
  // For custom ranges: dateRange = 'custom', startDate = 'YYYY-MM-DD', endDate = 'YYYY-MM-DD'
  static async fetchOverviewData(dateRange = 'last_week_sun_sat', startDate = null, endDate = null) {
    try {
      logInfo('ApiService.fetchOverviewData', 'Fetching overview data from server', { dateRange, startDate, endDate });

      let url = `${this.overviewUrl}/overview-data?date_range=${encodeURIComponent(dateRange)}`;
      if (dateRange === 'custom' && startDate && endDate) {
        url += `&start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`;
      }
      const headers = await getAuthHeaders();
      const response = await fetch(url, {
        headers,
        // Increased timeout for Supermetrics API calls (was 15s, now 45s)
        ...(typeof AbortSignal !== 'undefined' && AbortSignal.timeout ?
          { signal: AbortSignal.timeout(45000) } : {})
      });

      if (!response.ok) {
        const errorText = await response.text();
        logError(
          'ApiService.fetchOverviewData',
          `Server returned ${response.status}`,
          new Error(errorText),
          { dateRange, status: response.status }
        );
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      logInfo('ApiService.fetchOverviewData', 'Successfully loaded overview data from server', {
        dateRange,
        source: data.meta?.source,
        generatedAt: data.meta?.generatedAt
      });
      return data;

    } catch (error) {
      logError(
        'ApiService.fetchOverviewData',
        'Failed to fetch overview data',
        error,
        { dateRange }
      );
      throw error;
    }
  }

  // ============================================================================
  // WEEKLY REPORTS API
  // ============================================================================

  // List all weekly reports for the current organization
  static async listWeeklyReports() {
    try {
      logInfo('ApiService.listWeeklyReports', 'Fetching weekly reports list');

      const url = `${this.reportsUrl}/weekly-reports`;
      const headers = await getAuthHeaders();
      const response = await fetch(url, {
        headers,
        ...(typeof AbortSignal !== 'undefined' && AbortSignal.timeout ?
          { signal: AbortSignal.timeout(15000) } : {})
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      logInfo('ApiService.listWeeklyReports', 'Successfully loaded weekly reports', {
        count: data.reports?.length || 0
      });
      return data;

    } catch (error) {
      logError('ApiService.listWeeklyReports', 'Failed to fetch weekly reports', error);
      throw error;
    }
  }

  // Get a specific weekly report by year-week
  static async getWeeklyReport(yearWeek) {
    try {
      logInfo('ApiService.getWeeklyReport', 'Fetching weekly report', { yearWeek });

      const url = `${this.baseUrl}/weekly-reports/${encodeURIComponent(yearWeek)}`;
      const headers = await getAuthHeaders();
      const response = await fetch(url, {
        headers,
        ...(typeof AbortSignal !== 'undefined' && AbortSignal.timeout ?
          { signal: AbortSignal.timeout(15000) } : {})
      });

      if (response.status === 404) {
        logInfo('ApiService.getWeeklyReport', 'Weekly report not found', { yearWeek });
        return null;
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      logInfo('ApiService.getWeeklyReport', 'Successfully loaded weekly report', { yearWeek });
      return data;

    } catch (error) {
      logError('ApiService.getWeeklyReport', 'Failed to fetch weekly report', error, { yearWeek });
      throw error;
    }
  }

  // Create or update a weekly report
  static async saveWeeklyReport(reportData) {
    try {
      logInfo('ApiService.saveWeeklyReport', 'Saving weekly report', {
        yearWeek: reportData.yearWeek
      });

      const url = `${this.reportsUrl}/weekly-reports`;
      const headers = await getAuthHeaders();
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reportData),
        ...(typeof AbortSignal !== 'undefined' && AbortSignal.timeout ?
          { signal: AbortSignal.timeout(30000) } : {})
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      logInfo('ApiService.saveWeeklyReport', 'Successfully saved weekly report', {
        yearWeek: reportData.yearWeek
      });
      return data;

    } catch (error) {
      logError('ApiService.saveWeeklyReport', 'Failed to save weekly report', error, {
        yearWeek: reportData.yearWeek
      });
      throw error;
    }
  }

  // Update only the insights for a weekly report
  static async updateWeeklyReportInsights(yearWeek, insights) {
    try {
      logInfo('ApiService.updateWeeklyReportInsights', 'Updating weekly report insights', { yearWeek });

      const url = `${this.baseUrl}/weekly-reports/${encodeURIComponent(yearWeek)}/insights`;
      const headers = await getAuthHeaders();
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(insights),
        ...(typeof AbortSignal !== 'undefined' && AbortSignal.timeout ?
          { signal: AbortSignal.timeout(15000) } : {})
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      logInfo('ApiService.updateWeeklyReportInsights', 'Successfully updated insights', { yearWeek });
      return data;

    } catch (error) {
      logError('ApiService.updateWeeklyReportInsights', 'Failed to update insights', error, { yearWeek });
      throw error;
    }
  }

  // Fetch raw data for weekly report generation (Meta + Google)
  static async fetchWeeklyReportRawData(startDate, endDate) {
    try {
      logInfo('ApiService.fetchWeeklyReportRawData', 'Fetching raw data for weekly report', {
        startDate,
        endDate
      });

      // Fetch Meta and Google data in parallel
      const [metaResult, googleResult] = await Promise.allSettled([
        this.fetchLiveSupermetricsData({
          platform: 'meta',
          startDate,
          endDate,
          search: '',
          sortBy: 'cost',
          sortOrder: 'desc',
          minSpend: 0,
          minImpressions: 0
        }),
        this.fetchLiveSupermetricsData({
          platform: 'google',
          startDate,
          endDate,
          search: '',
          sortBy: 'cost',
          sortOrder: 'desc',
          minSpend: 0,
          minImpressions: 0
        })
      ]);

      const metaData = metaResult.status === 'fulfilled' ? metaResult.value : null;
      const googleData = googleResult.status === 'fulfilled' ? googleResult.value : null;

      if (metaResult.status === 'rejected') {
        logWarning('ApiService.fetchWeeklyReportRawData', 'Failed to fetch Meta data', {
          error: metaResult.reason?.message
        });
      }

      if (googleResult.status === 'rejected') {
        logWarning('ApiService.fetchWeeklyReportRawData', 'Failed to fetch Google data', {
          error: googleResult.reason?.message
        });
      }

      logInfo('ApiService.fetchWeeklyReportRawData', 'Completed fetching raw data', {
        metaCount: metaData?.items?.length || 0,
        googleCount: googleData?.items?.length || 0
      });

      return {
        meta: metaData,
        google: googleData,
        fetchedAt: new Date().toISOString()
      };

    } catch (error) {
      logError('ApiService.fetchWeeklyReportRawData', 'Failed to fetch weekly report data', error);
      throw error;
    }
  }

  // ============================================================================
  // GOOGLE ADS API CONNECTION
  // ============================================================================

  static googleAdsAuthUrl = `${EDGE_FUNCTIONS_BASE}/google-ads-auth`;
  static googleAdsAssetsUrl = `${EDGE_FUNCTIONS_BASE}/google-ads-assets`;

  /**
   * Get Google Ads connection status for the current organization
   */
  static async getGoogleAdsConnectionStatus() {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${this.googleAdsAuthUrl}/status`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        logWarning('ApiService.getGoogleAdsConnectionStatus', 'Failed to get status', {
          status: response.status,
          error: errorText
        });
        return { configured: false, connections: [], error: errorText };
      }

      return await response.json();
    } catch (error) {
      logError('ApiService.getGoogleAdsConnectionStatus', 'Error fetching connection status', error);
      return { configured: false, connections: [], error: error.message };
    }
  }

  /**
   * Get OAuth URL to connect Google Ads account
   */
  static async getGoogleAdsConnectUrl() {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${this.googleAdsAuthUrl}/connect`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to get connect URL');
      }

      const data = await response.json();
      return data.authUrl;
    } catch (error) {
      logError('ApiService.getGoogleAdsConnectUrl', 'Error getting connect URL', error);
      throw error;
    }
  }

  /**
   * Disconnect a Google Ads account
   */
  static async disconnectGoogleAds(connectionId) {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${this.googleAdsAuthUrl}/disconnect`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ connectionId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to disconnect');
      }

      return await response.json();
    } catch (error) {
      logError('ApiService.disconnectGoogleAds', 'Error disconnecting account', error);
      throw error;
    }
  }

  /**
   * Fetch assets from Google Ads API for a specific connection
   */
  static async fetchGoogleAdsAssets(connectionId, campaignTypes = null) {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${this.googleAdsAssetsUrl}/fetch`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          connectionId,
          campaignTypes: campaignTypes || ['PERFORMANCE_MAX', 'DEMAND_GEN', 'DISPLAY', 'VIDEO'],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch assets');
      }

      return await response.json();
    } catch (error) {
      logError('ApiService.fetchGoogleAdsAssets', 'Error fetching assets', error);
      throw error;
    }
  }

  /**
   * List stored Google Ads assets
   */
  static async listGoogleAdsAssets(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.campaignType) queryParams.set('campaignType', params.campaignType);
      if (params.dateRangeType) queryParams.set('dateRangeType', params.dateRangeType);
      if (params.limit) queryParams.set('limit', params.limit.toString());
      if (params.offset) queryParams.set('offset', params.offset.toString());

      const headers = await getAuthHeaders();
      const response = await fetch(`${this.googleAdsAssetsUrl}/list?${queryParams}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to list assets');
      }

      return await response.json();
    } catch (error) {
      logError('ApiService.listGoogleAdsAssets', 'Error listing assets', error);
      throw error;
    }
  }

  // ========================================================================
  // PINTEREST API CONNECTION
  // ========================================================================

  static pinterestAuthUrl = `${EDGE_FUNCTIONS_BASE}/pinterest-auth`;

  /**
   * Get Pinterest connection status for the current organization
   */
  static async getPinterestConnectionStatus() {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${this.pinterestAuthUrl}/status`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        logWarning('ApiService.getPinterestConnectionStatus', 'Failed to get status', {
          status: response.status,
          error: errorText
        });
        return { configured: false, connections: [], error: errorText };
      }

      return await response.json();
    } catch (error) {
      logError('ApiService.getPinterestConnectionStatus', 'Error fetching connection status', error);
      return { configured: false, connections: [], error: error.message };
    }
  }

  /**
   * Get OAuth URL to connect Pinterest account
   */
  static async getPinterestConnectUrl() {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${this.pinterestAuthUrl}/connect`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to get connect URL');
      }

      const data = await response.json();
      return data.authUrl;
    } catch (error) {
      logError('ApiService.getPinterestConnectUrl', 'Error getting connect URL', error);
      throw error;
    }
  }

  /**
   * Disconnect a Pinterest account
   */
  static async disconnectPinterest(connectionId) {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${this.pinterestAuthUrl}/disconnect`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ connectionId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to disconnect');
      }

      return await response.json();
    } catch (error) {
      logError('ApiService.disconnectPinterest', 'Error disconnecting account', error);
      throw error;
    }
  }

  // ========================================================================
  // PINTEREST ADS
  // ========================================================================

  static async fetchPinterestCreatives(params = {}) {
    try {
      logInfo('ApiService.fetchPinterestCreatives', 'Fetching Pinterest data from server', { params });

      const requestKey = `pinterest-creatives-${JSON.stringify(params)}`;
      if (this.activeRequests.has(requestKey)) {
        logInfo('ApiService.fetchPinterestCreatives', 'Request already in progress, reusing promise', { requestKey });
        return await this.activeRequests.get(requestKey);
      }

      const requestPromise = this._fetchPinterestCreativesInternal(params);
      this.activeRequests.set(requestKey, requestPromise);

      try {
        const result = await requestPromise;
        return result;
      } finally {
        this.activeRequests.delete(requestKey);
      }
    } catch (error) {
      logError('ApiService.fetchPinterestCreatives', 'Error fetching Pinterest creatives', error, { params });
      throw error;
    }
  }

  static async _fetchPinterestCreativesInternal(params = {}) {
    const endpoint = `${this.pinterestUrl}/creatives`;
    try {
      const queryParams = new URLSearchParams();
      if (params.dateRange) queryParams.append('dateRange', params.dateRange);
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.minSpend) queryParams.append('minSpend', params.minSpend.toString());
      if (params.minImpressions) queryParams.append('minImpressions', params.minImpressions.toString());
      if (params.adStatus) queryParams.append('adStatus', params.adStatus);

      const url = `${endpoint}?${queryParams.toString()}`;
      logInfo('ApiService._fetchPinterestCreativesInternal', 'Starting Pinterest fetch', { url, params });

      const headers = await getAuthHeaders();
      const response = await fetch(url, {
        headers,
        ...(typeof AbortSignal !== 'undefined' && AbortSignal.timeout ?
          { signal: AbortSignal.timeout(15000) } : {})
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      logInfo('ApiService._fetchPinterestCreativesInternal', 'Successfully loaded Pinterest data', {
        rowCount: data.meta?.rowCount,
      });
      return data;
    } catch (error) {
      logError('ApiService._fetchPinterestCreativesInternal', 'Pinterest API error', error, { params, endpoint });
      throw error;
    }
  }

  static async fetchPinterestRollup(dateRangeType, adStatus = 'ACTIVE') {
    try {
      const requestKey = `pinterest-rollup-${dateRangeType}-${adStatus}`;

      if (this.activeRequests.has(requestKey)) {
        logInfo('ApiService.fetchPinterestRollup', 'Request already in progress, reusing promise', { requestKey });
        return await this.activeRequests.get(requestKey);
      }

      const requestPromise = this._fetchPinterestRollupInternal(dateRangeType, adStatus);
      this.activeRequests.set(requestKey, requestPromise);

      try {
        const result = await requestPromise;
        return result;
      } finally {
        this.activeRequests.delete(requestKey);
      }
    } catch (error) {
      logError('ApiService.fetchPinterestRollup', 'Pinterest rollup error', error, { dateRangeType });
      return {
        rollupData: null,
        meta: {
          fallback: true,
          fallbackReason: error.message,
          dateRangeType,
          source: 'error'
        }
      };
    }
  }

  static async _fetchPinterestRollupInternal(dateRangeType, adStatus = 'ACTIVE') {
    const url = `${this.pinterestUrl}/pinterest-rollup?date_range_type=${dateRangeType}&adStatus=${adStatus}`;
    logInfo('ApiService._fetchPinterestRollupInternal', 'Fetching Pinterest rollup', { dateRangeType, adStatus, url });

    const headers = await getAuthHeaders();
    const response = await fetch(url, {
      headers,
      ...(typeof AbortSignal !== 'undefined' && AbortSignal.timeout ?
        { signal: AbortSignal.timeout(20000) } : {})
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server returned ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    logInfo('ApiService._fetchPinterestRollupInternal', 'Pinterest rollup data received', {
      dateRangeType,
      cost_usd: data.rollupData?.cost_usd,
      source: data.meta?.source
    });
    return data;
  }
}

export default ApiService;
